import type * as hast from 'hast';
import { visit } from 'unist-util-visit';

import type { Block } from '@gitbook/slate';

import type { ParsingContext } from '../context';
import { getBooleanAttribute } from './utils';

/**
 * Extract the footnote definitions from a hast tree,
 * and remove them from the tree.
 */
export async function parseFootnotes(
    tree: hast.Root,
    parsingContext: ParsingContext,
    parseNodes: (node: hast.Element) => Promise<Block[]>
) {
    const pendingFootnotes: Promise<void>[] = [];
    visit(
        tree,
        (element: hast.Element) => isFootnotesDefinitionSection(element),
        (section: hast.Element, index, parent) => {
            const list = section.children.find(
                (child) => child.type === 'element' && child.tagName === 'ol'
            );

            if (!list || list.type !== 'element') {
                return;
            }

            for (const node of list.children) {
                if (node.type !== 'element') {
                    return;
                }

                const ref = node.properties?.id as string;

                if (typeof ref === 'string') {
                    pendingFootnotes.push(
                        (async () => {
                            // Register the footnote definition as a method to retrieve the content.
                            // This is useful when the same footnote is referenced multiple times so you
                            // get unique content for each reference.
                            parsingContext.setDefinitionForFootnoteId(ref, () => parseNodes(node));
                        })()
                    );
                }
            }

            // Remove the footnote section from the tree
            if (parent && typeof index === 'number') {
                parent.children.splice(index, 1);
            }
        }
    );

    await Promise.all(pendingFootnotes);
}

/**
 * Check if a node is a footnotes definition section
 */
export function isFootnotesDefinitionSection(node: hast.Element) {
    return (
        node.type === 'element' &&
        node.tagName === 'section' &&
        getBooleanAttribute(node.properties, 'dataFootnotes')
    );
}
